import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { toggleCreatorModal } from '../../redux/appConfigurator/action';
import { RootState } from '../../redux/root-reducer';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { SandboxPrivateProjectPicker } from './SandboxPrivateProjectPicker';
import type { IProject } from '../../types/global/helper';
import { type SandboxAssignmentSubmission } from '../../utils/sandboxCourseApi';
import { fetchMySandboxLessonAssignments, submitSandboxAssignment } from '../../redux/sandbox/actions';

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        bgcolor: '#323232',
        color: OBSIDIAN.onSurface,
        '& fieldset': { borderColor: `${OBSIDIAN.outline}55` },
    },
    '& label': { color: OBSIDIAN.onSurfaceVariant },
};

type SelectedProject = { id: string; title: string };

type Props = {
    courseId: string;
    lessonId: string;
    brief?: string;
};

function statusLabel(status: string) {
    switch (status) {
        case 'submitted':
            return 'Submitted — awaiting instructor';
        case 'in_review':
            return 'In review';
        case 'passed':
            return 'Passed';
        case 'revision_requested':
            return 'Revision requested';
        case 'rejected':
            return 'Not passed';
        default:
            return status;
    }
}

function statusColor(status: string) {
    switch (status) {
        case 'passed':
            return OBSIDIAN.secondary;
        case 'revision_requested':
        case 'rejected':
            return '#ff7351';
        default:
            return OBSIDIAN.primary;
    }
}

function projectTitle(sub: SandboxAssignmentSubmission) {
    if (sub.project && typeof sub.project === 'object') return sub.project.title || 'Project';
    return 'Project';
}

/**
 * Student assignment: upload/select + notes, then view status & instructor feedback.
 */
export function SandboxAssignmentSubmitPanel({ courseId, lessonId, brief }: Props) {
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const currentProjectInCreation = useSelector(
        (s: RootState) => s.project?.currentProjectInCreation as IProject | undefined,
    );
    const [pickerOpen, setPickerOpen] = useState(false);
    const [selected, setSelected] = useState<SelectedProject | null>(null);
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submissions, setSubmissions] = useState<SandboxAssignmentSubmission[]>([]);
    const [resubmitting, setResubmitting] = useState(false);
    const awaitingUploadRef = useRef(false);
    const lastPickedIdRef = useRef<string | null>(null);

    const latest = submissions[0];
    const canSubmitForm =
        resubmitting || !latest || latest.status === 'revision_requested' || latest.status === 'rejected';

    const load = useCallback(async () => {
        if (!courseId || !lessonId) return;
        setLoading(true);
        try {
            const res: any = await dispatchPromise(fetchMySandboxLessonAssignments({ courseId, lessonId }));
            const rows = (res?.data?.submissions || res?.submissions || []) as SandboxAssignmentSubmission[];
            setSubmissions(Array.isArray(rows) ? rows : []);
        } catch {
            setSubmissions([]);
        } finally {
            setLoading(false);
        }
    }, [courseId, lessonId, dispatchPromise]);

    useEffect(() => {
        setSelected(null);
        setNotes('');
        setResubmitting(false);
        void load();
    }, [load]);

    useEffect(() => {
        if (!awaitingUploadRef.current || !currentProjectInCreation?._id) return;
        const id = String(currentProjectInCreation._id);
        if (lastPickedIdRef.current === id) return;
        lastPickedIdRef.current = id;
        awaitingUploadRef.current = false;
        setSelected({
            id,
            title: currentProjectInCreation.title || 'Untitled project',
        });
        if (currentProjectInCreation.public_status && currentProjectInCreation.public_status !== 'private') {
            showToastMessage('Tip: set the project to Private so only you and your instructor can see it', 'info');
        } else {
            showToastMessage('Project ready — add notes and submit', 'success');
        }
    }, [currentProjectInCreation]);

    const openUpload = () => {
        awaitingUploadRef.current = true;
        showToastMessage('Upload as a private project for instructor review', 'info');
        dispatch(toggleCreatorModal({ creatorType: 'project' }));
    };

    const handleSubmit = async () => {
        if (!selected?.id) {
            showToastMessage('Select or upload a private project first', 'error');
            return;
        }
        setSubmitting(true);
        try {
            await dispatchPromise(
                submitSandboxAssignment({
                    courseId,
                    lessonId,
                    body: { project: selected.id, notes },
                }),
            );
            showToastMessage('Submitted for instructor review', 'success');
            setSelected(null);
            setNotes('');
            setResubmitting(false);
            await load();
        } catch (e: any) {
            showToastMessage(e?.message || 'Submit failed', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} sx={{ color: OBSIDIAN.primary }} />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography
                sx={{
                    fontFamily: OBSIDIAN_DISPLAY_FONT,
                    fontWeight: 700,
                    color: OBSIDIAN.onSurface,
                }}>
                Assignment
            </Typography>
            <Typography
                sx={{
                    fontFamily: OBSIDIAN_BODY_FONT,
                    fontSize: 13,
                    color: OBSIDIAN.onSurfaceVariant,
                }}>
                {brief || 'Upload a private cut from here, or pick one you already have — then add director notes.'}
            </Typography>

            {submissions.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                            fontWeight: 700,
                            fontSize: 12,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: OBSIDIAN.onSurfaceVariant,
                        }}>
                        Your submissions
                    </Typography>
                    {submissions.map(sub => (
                        <Box
                            key={sub._id}
                            sx={{
                                p: 1.75,
                                borderRadius: 2,
                                bgcolor: OBSIDIAN.surfaceContainer,
                                border: `1px solid ${OBSIDIAN.outline}44`,
                            }}>
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: statusColor(sub.status),
                                    fontFamily: OBSIDIAN_BODY_FONT,
                                    mb: 0.5,
                                }}>
                                {statusLabel(sub.status)}
                                {typeof sub.teacher_rating === 'number' ? ` · ${sub.teacher_rating}/5` : ''}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: OBSIDIAN.onSurface,
                                    fontFamily: OBSIDIAN_BODY_FONT,
                                }}>
                                {projectTitle(sub)}
                            </Typography>
                            {sub.notes ? (
                                <Typography
                                    sx={{
                                        mt: 0.75,
                                        fontSize: 12,
                                        color: OBSIDIAN.onSurfaceVariant,
                                        fontFamily: OBSIDIAN_BODY_FONT,
                                        whiteSpace: 'pre-wrap',
                                    }}>
                                    Your notes: {sub.notes}
                                </Typography>
                            ) : null}
                            {sub.teacher_review ? (
                                <Box
                                    sx={{
                                        mt: 1.25,
                                        p: 1.25,
                                        borderRadius: 1.5,
                                        bgcolor: OBSIDIAN.surfaceContainerHighest,
                                        borderLeft: `3px solid ${statusColor(sub.status)}`,
                                    }}>
                                    <Typography
                                        sx={{
                                            fontSize: 11,
                                            fontWeight: 700,
                                            letterSpacing: '0.06em',
                                            textTransform: 'uppercase',
                                            color: OBSIDIAN.onSurfaceVariant,
                                            mb: 0.5,
                                        }}>
                                        Instructor feedback
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            color: OBSIDIAN.onSurface,
                                            fontFamily: OBSIDIAN_BODY_FONT,
                                            whiteSpace: 'pre-wrap',
                                        }}>
                                        {sub.teacher_review}
                                    </Typography>
                                </Box>
                            ) : null}
                        </Box>
                    ))}
                </Box>
            )}

            {canSubmitForm ? (
                <>
                    {latest &&
                    (latest.status === 'revision_requested' || latest.status === 'rejected') &&
                    !resubmitting ? (
                        <Button
                            onClick={() => setResubmitting(true)}
                            sx={{
                                alignSelf: 'flex-start',
                                textTransform: 'none',
                                fontFamily: OBSIDIAN_BODY_FONT,
                                fontWeight: 700,
                                color: OBSIDIAN.primary,
                                px: 0,
                            }}>
                            Submit a revision
                        </Button>
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                <Button
                                    startIcon={<CloudUploadOutlinedIcon />}
                                    onClick={openUpload}
                                    sx={{
                                        textTransform: 'none',
                                        fontFamily: OBSIDIAN_BODY_FONT,
                                        fontWeight: 700,
                                        borderRadius: 999,
                                        bgcolor: OBSIDIAN.primary,
                                        color: OBSIDIAN.onPrimaryFixed,
                                        px: 2,
                                        '&:hover': { filter: 'brightness(1.05)', bgcolor: OBSIDIAN.primary },
                                    }}>
                                    Upload project
                                </Button>
                                <Button
                                    startIcon={<VideoLibraryOutlinedIcon />}
                                    onClick={() => setPickerOpen(true)}
                                    sx={{
                                        textTransform: 'none',
                                        fontFamily: OBSIDIAN_BODY_FONT,
                                        fontWeight: 600,
                                        borderRadius: 999,
                                        border: `1px solid ${OBSIDIAN.outline}66`,
                                        color: OBSIDIAN.onSurface,
                                        px: 2,
                                        '&:hover': { bgcolor: OBSIDIAN.surfaceContainerHigh },
                                    }}>
                                    Select private project
                                </Button>
                            </Box>

                            {selected ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        p: 1.5,
                                        borderRadius: 2,
                                        bgcolor: OBSIDIAN.surfaceContainer,
                                        border: `1px solid ${OBSIDIAN.outline}44`,
                                    }}>
                                    <LockOutlinedIcon sx={{ fontSize: 18, color: OBSIDIAN.secondary }} />
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                            noWrap
                                            sx={{
                                                fontFamily: OBSIDIAN_BODY_FONT,
                                                fontWeight: 600,
                                                fontSize: 14,
                                                color: OBSIDIAN.onSurface,
                                            }}>
                                            {selected.title}
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: OBSIDIAN.onSurfaceVariant }}>
                                            Ready to submit
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        onClick={() => setSelected(null)}
                                        sx={{
                                            textTransform: 'none',
                                            color: OBSIDIAN.onSurfaceVariant,
                                            minWidth: 0,
                                        }}>
                                        Clear
                                    </Button>
                                </Box>
                            ) : null}

                            <TextField
                                label="Director notes"
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                multiline
                                minRows={3}
                                fullWidth
                                sx={fieldSx}
                            />

                            <Button
                                variant="contained"
                                disabled={submitting || !selected?.id}
                                onClick={() => void handleSubmit()}
                                sx={{
                                    bgcolor: OBSIDIAN.primary,
                                    color: OBSIDIAN.onPrimaryFixed,
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    borderRadius: 999,
                                    alignSelf: 'flex-start',
                                    '&.Mui-disabled': { opacity: 0.45, color: OBSIDIAN.onPrimaryFixed },
                                }}>
                                {submitting
                                    ? 'Submitting…'
                                    : latest
                                    ? 'Submit revision'
                                    : 'Submit for instructor review'}
                            </Button>
                        </>
                    )}
                </>
            ) : null}

            <SandboxPrivateProjectPicker
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                title="Select your cut"
                onSelect={project => {
                    setSelected({
                        id: String(project._id),
                        title: project.title || 'Untitled project',
                    });
                }}
                onUploadNew={() => {
                    setPickerOpen(false);
                    openUpload();
                }}
            />
        </Box>
    );
}
